import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import usePermissions from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../config/permissions';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

export default function AdminIncomeExpenses() {
  const { canEdit, canAdd, canDelete } = usePermissions();
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState({ total_income: '0', total_expense: '0', net: '0' });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [filterType, setFilterType] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [formData, setFormData] = useState({
    entry_type: 'income', category: 'dues', title: '', description: '', amount: '', date: '',
  });

  const fetchData = useCallback(() => {
    const params = filterType ? `?entry_type=${filterType}` : '';
    Promise.all([
      api.get(`/admin/income-expenses/${params}`),
      api.get('/admin/income-expenses/summary/'),
    ]).then(([entriesRes, summaryRes]) => {
      setEntries(entriesRes.data.results || entriesRes.data);
      setSummary(summaryRes.data);
    }).finally(() => setLoading(false));
  }, [filterType]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setFormData({ entry_type: 'income', category: 'dues', title: '', description: '', amount: '', date: '' });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        const res = await api.put(`/admin/income-expenses/${editingId}/`, formData);
        setEntries(entries.map(en => en.id === editingId ? res.data : en));
        toast.success('Entry updated!');
      } else {
        const res = await api.post('/admin/income-expenses/', formData);
        setEntries([res.data, ...entries]);
        toast.success('Entry recorded!');
      }
      resetForm();
      fetchData();
    } catch { toast.error('Failed to save entry.'); }
  };

  const handleEdit = (entry) => {
    setFormData({
      entry_type: entry.entry_type, category: entry.category, title: entry.title,
      description: entry.description || '', amount: entry.amount, date: entry.date,
    });
    setEditingId(entry.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await api.delete(`/admin/income-expenses/${id}/`);
      setEntries(entries.filter(e => e.id !== id));
      toast.success('Entry deleted.');
      fetchData();
    } catch { toast.error('Delete failed.'); }
  };

  const categories = {
    income: ['dues', 'registration', 'event', 'donation', 'grant', 'other'],
    expense: ['salary', 'logistics', 'supplies', 'venue', 'travel', 'utility', 'other'],
  };

  // Client-side search + date range filtering
  const filteredEntries = entries.filter((e) => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matches = (e.title || '').toLowerCase().includes(q)
        || (e.description || '').toLowerCase().includes(q)
        || (e.category || '').toLowerCase().includes(q)
        || (e.receipt_number || '').toLowerCase().includes(q);
      if (!matches) return false;
    }
    if (dateFrom && e.date < dateFrom) return false;
    if (dateTo && e.date > dateTo) return false;
    return true;
  });

  const downloadPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text('Income & Expenses Report', 14, 18);
    doc.setFontSize(10);
    const subtitle = [
      dateFrom && dateTo ? `${dateFrom} to ${dateTo}` : dateFrom ? `From ${dateFrom}` : dateTo ? `Up to ${dateTo}` : 'All dates',
      filterType ? filterType.charAt(0).toUpperCase() + filterType.slice(1) : 'All types',
    ].join(' | ');
    doc.text(subtitle, 14, 26);

    doc.setFontSize(9);
    doc.setTextColor(34, 139, 34);
    doc.text(`Income: ₦${Number(summary.total_income).toLocaleString()}`, 14, 33);
    doc.setTextColor(220, 20, 20);
    doc.text(`Expenses: ₦${Number(summary.total_expense).toLocaleString()}`, 80, 33);
    doc.setTextColor(0);
    doc.text(`Net: ₦${Number(summary.net).toLocaleString()}`, 150, 33);

    autoTable(doc, {
      startY: 38,
      head: [['Date', 'Type', 'Category', 'Title', 'Amount (₦)', 'Receipt #']],
      body: filteredEntries.map(e => [
        e.date,
        e.entry_type,
        e.category,
        e.title,
        Number(e.amount).toLocaleString(),
        e.receipt_number || '—',
      ]),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [11, 93, 30] },
    });

    doc.save('income_expenses_report.pdf');
  };

  const exportExcel = () => {
    const data = filteredEntries.map(e => ({
      Date: e.date,
      Type: e.entry_type,
      Category: e.category,
      Title: e.title,
      Description: e.description || '',
      'Amount (₦)': Number(e.amount),
      'Receipt #': e.receipt_number || '',
      'Recorded By': e.recorded_by_name || '',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Income & Expenses');
    XLSX.writeFile(wb, 'income_expenses_report.xlsx');
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Income & Expenses</h1>
          <div className="flex gap-4 mt-1 text-sm">
            <span className="text-green-600 font-semibold">Income: ₦{Number(summary.total_income).toLocaleString()}</span>
            <span className="text-red-600 font-semibold">Expenses: ₦{Number(summary.total_expense).toLocaleString()}</span>
            <span className="text-[#0B5D1E] font-bold">Net: ₦{Number(summary.net).toLocaleString()}</span>
          </div>
        </div>
        <div className="flex gap-3">
          <select value={filterType} onChange={e => setFilterType(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0B5D1E] outline-none">
            <option value="">All Types</option>
            <option value="income">Income</option>
            <option value="expense">Expense</option>
          </select>
          {canAdd(PERMISSIONS.ADD_INCOME_EXPENSES) && (
            <button onClick={() => { resetForm(); setShowForm(!showForm); }}
              className="bg-[#0B5D1E] text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-[#062E10] transition">
              {showForm ? 'Cancel' : 'Add Entry'}
            </button>
          )}
        </div>
      </div>

      {/* Search bar, date range & export buttons */}
      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <div className="flex-1 min-w-[200px]">
          <input type="text" placeholder="Search title, category, receipt..."
            value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0B5D1E] outline-none" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">From</label>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0B5D1E] outline-none" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">To</label>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0B5D1E] outline-none" />
        </div>
        {(searchQuery || dateFrom || dateTo) && (
          <button onClick={() => { setSearchQuery(''); setDateFrom(''); setDateTo(''); }}
            className="px-3 py-2 text-sm text-gray-600 hover:text-red-600 border rounded-lg">Clear</button>
        )}
        <button onClick={downloadPDF}
          className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition flex items-center gap-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17v3a2 2 0 002 2h14a2 2 0 002-2v-3" /></svg>
          PDF
        </button>
        <button onClick={exportExcel}
          className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition flex items-center gap-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17v3a2 2 0 002 2h14a2 2 0 002-2v-3" /></svg>
          Excel
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Type</label>
              <select value={formData.entry_type} onChange={e => setFormData({ ...formData, entry_type: e.target.value, category: '' })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none">
                <option value="income">Income</option>
                <option value="expense">Expense</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Category</label>
              <select value={formData.category} onChange={e => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none">
                <option value="">Select...</option>
                {(categories[formData.entry_type] || []).map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Title</label>
              <input type="text" required value={formData.title}
                onChange={e => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Amount (₦)</label>
              <input type="number" step="0.01" required value={formData.amount}
                onChange={e => setFormData({ ...formData, amount: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Date</label>
              <input type="date" required value={formData.date}
                onChange={e => setFormData({ ...formData, date: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none" />
            </div>
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-500 mb-1">Description</label>
              <textarea value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none" rows={2} />
            </div>
          </div>
          <button type="submit" className="mt-4 bg-[#0B5D1E] text-white px-6 py-2 rounded-lg font-semibold">
            {editingId ? 'Update' : 'Record'}
          </button>
        </form>
      )}

      <div className="bg-white rounded-xl shadow-sm border overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Type</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Category</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Title</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Amount</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Receipt</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? (
              <tr><td colSpan="7" className="text-center py-8 text-gray-500">Loading...</td></tr>
            ) : filteredEntries.length === 0 ? (
              <tr><td colSpan="7" className="text-center py-8 text-gray-400">No entries found.</td></tr>
            ) : filteredEntries.map(e => (
              <tr key={e.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm">{e.date}</td>
                <td className="px-6 py-4">
                  <span className={`text-xs px-2 py-1 rounded-full font-semibold ${
                    e.entry_type === 'income' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>{e.entry_type}</span>
                </td>
                <td className="px-6 py-4 text-sm capitalize">{e.category}</td>
                <td className="px-6 py-4 text-sm">{e.title}</td>
                <td className="px-6 py-4 text-sm font-semibold">₦{Number(e.amount).toLocaleString()}</td>
                <td className="px-6 py-4 text-sm font-mono">{e.receipt_number || '—'}</td>
                <td className="px-6 py-4 flex gap-2">
                  {canEdit(PERMISSIONS.EDIT_INCOME_EXPENSES) && (
                    <button onClick={() => handleEdit(e)} className="text-blue-600 hover:underline text-sm">Edit</button>
                  )}
                  {canDelete(PERMISSIONS.DELETE_INCOME_EXPENSES) && (
                    <button onClick={() => handleDelete(e.id)} className="text-red-600 hover:underline text-sm">Delete</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
